import React, { useState } from 'react';
import { Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { leadsApi } from '../../lib/api';
import { trackGenerateLead } from '../../lib/analytics';

export const LeadCaptureForm = ({ 
  source = 'website',
  variant = 'default', // 'default', 'inline', 'minimal'
  title = 'Join the Dojo',
  description = 'Get exclusive insights on biohacking, meditation, and peak performance.',
  buttonText = 'Get Access',
  interests = [],
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await leadsApi.create({
        email: formData.email,
        first_name: formData.first_name || null,
        source,
        interests,
      });
      
      setSuccess(true);
      trackGenerateLead(source);
      setFormData({ email: '', first_name: '' });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Lead capture error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success State
  if (success) {
    return (
      <div 
        className={`${variant === 'minimal' ? '' : 'p-6 md:p-8 glass-panel'}`}
        data-testid="lead-form-success"
      >
        <div className="flex flex-col items-center text-center">
          <CheckCircle className="w-12 h-12 text-[#C7FA3E] mb-4" />
          <h3 
            className="text-xl text-white mb-2"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            You're In
          </h3>
          <p className="text-zinc-400 text-sm">
            Welcome to the dojo. Check your inbox for exclusive content.
          </p>
        </div>
      </div>
    );
  }

  // Inline Variant
  if (variant === 'inline') {
    return (
      <form 
        onSubmit={handleSubmit} 
        className="flex flex-col sm:flex-row gap-3"
        data-testid="lead-capture-form-inline"
      >
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="Enter your email"
          className="input-ninja flex-1 px-4 text-sm text-white placeholder-zinc-600"
          data-testid="lead-email-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="btn-lime-capture whitespace-nowrap flex items-center justify-center gap-2"
          data-testid="lead-submit-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              {buttonText}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
        {error && (
          <p className="text-red-500 text-sm mt-2 sm:col-span-2">{error}</p>
        )}
      </form>
    );
  }

  // Minimal Variant
  if (variant === 'minimal') {
    return (
      <form 
        onSubmit={handleSubmit} 
        className="space-y-3"
        data-testid="lead-capture-form-minimal"
      >
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="Enter your email"
          className="input-ninja w-full px-4 text-sm text-white placeholder-zinc-600"
          data-testid="lead-email-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="btn-lime-capture w-full flex items-center justify-center gap-2"
          data-testid="lead-submit-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            buttonText
          )}
        </button>
        {error && <p className="text-red-500 text-sm">{error}</p>}
      </form>
    );
  }

  // Default Variant
  return (
    <div 
      className="p-6 md:p-8 glass-panel"
      data-testid="lead-capture-form-default"
    >
      <h3 
        className="text-2xl text-white mb-2"
        style={{ fontFamily: 'Oswald, sans-serif' }}
      >
        {title}
      </h3>
      <p className="text-zinc-400 text-sm mb-6">{description}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          value={formData.first_name}
          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
          placeholder="First name (optional)"
          className="input-ninja w-full px-4 text-sm text-white placeholder-zinc-600"
          data-testid="lead-name-input"
        />
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="Enter your email"
          className="input-ninja w-full px-4 text-sm text-white placeholder-zinc-600"
          data-testid="lead-email-input"
          required
        />
        <button
          type="submit"
          disabled={loading}
          className="btn-lime-capture w-full flex items-center justify-center gap-2"
          data-testid="lead-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Joining...
            </>
          ) : (
            <>
              {buttonText}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </form>

      <p className="text-zinc-600 text-xs mt-4">
        We respect your privacy. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default LeadCaptureForm;
